/**
 * Newsletter on/off for signed-in users (/settings/notifications). Syncs the
 * same `newsletterSubscriptions` row the public form and the signed
 * preference center use, keyed by the account's VERIFIED email, so all three
 * surfaces always agree. Subscribing here needs no second confirmation: Clerk
 * already verified the address.
 */
import { useConvexAuth, useMutation, useQuery } from 'convex/react'
import { useState } from 'react'
import { api } from '../../convex/_generated/api'
import { trackEvent } from '../lib/analytics'
import { useTranslation } from '../lib/i18n'

export function SettingsNewsletter() {
  const { t, locale } = useTranslation()
  // Wait for Convex to hold the Clerk JWT, else the first answer is "no
  // identity" and the panel would flash the unavailable state.
  const { isAuthenticated } = useConvexAuth()
  const subscription = useQuery(
    api.newsletter.mySubscription,
    isAuthenticated ? {} : 'skip',
  )
  const setSubscription = useMutation(api.newsletter.setMySubscription)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  if (subscription === undefined) return null
  const subscribed = subscription?.status === 'active'

  async function toggle() {
    setBusy(true)
    setError(null)
    try {
      const result = await setSubscription({ subscribed: !subscribed, locale })
      if (result.status === 'unavailable') setError(t('email.settings.unavailable'))
      else if (result.status === 'rate_limited') {
        setError(t('pricing.founding.rateLimited'))
      } else if (result.status === 'active' || result.status === 'pending') {
        trackEvent('newsletter_signup_requested', {
          source: 'account_settings',
          status: result.status,
        })
      }
    } catch {
      setError(t('forms.genericError'))
    } finally {
      setBusy(false)
    }
  }

  return (
    <section className="settings-newsletter" aria-labelledby="settings-newsletter-title">
      <h3 id="settings-newsletter-title">{t('email.settings.newsletterTitle')}</h3>
      <p>
        <strong>{t('email.settings.newsletterLabel')}</strong>
        <br />
        {t('email.settings.newsletterHint')}
      </p>
      <p role="status" data-newsletter-subscribed={subscribed}>
        {subscription === null
          ? t('email.settings.unavailable')
          : subscribed
            ? t('email.settings.newsletterOn')
            : t('email.settings.newsletterOff')}
      </p>
      {subscription === null ? null : (
        <button
          type="button"
          className={subscribed ? 'ghost-button' : 'primary-button'}
          onClick={() => void toggle()}
          disabled={busy}
        >
          {busy
            ? t('email.settings.saving')
            : subscribed
              ? t('email.settings.unsubscribe')
              : t('email.settings.subscribe')}
        </button>
      )}
      {error ? (
        <p className="form-status is-error" role="status">
          {error}
        </p>
      ) : null}
    </section>
  )
}
